import { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { X, Send, Image, Loader2, Settings, Download, Key, Paperclip } from 'lucide-react';
import BugReportButton from './BugReportButton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const AVATAR_URL = "https://static.prod-images.emergentagent.com/jobs/795be194-f17c-410a-afd4-0981af84f9b2/images/1b0561bd0f3b65f0c0becc89c2366f65043aecc0ce812b2fd9f0d1a43b7d93e7.png";

const WELCOME_MESSAGE = `Hi! I'm your Heavenly Assistant! Here's what I can do for you:

\u2728 **Generate Maps** - Create architectural floor plans and site maps from your descriptions

\u{1f3a8} **Generate Pictures** - Create beautiful images of resort areas, rooms, and facilities

\u{1f50d} **Generate Visualizations** - Produce detailed visual representations of renovation concepts

\u{1f3d7}\ufe0f **Generate Renders** - Create photorealistic renders of reconstruction plans and before/after comparisons

\u{1f41b} **Spot a bug?** Tap the red button next to my icon — it goes straight to the team. Even a single sentence helps me get smarter.

Just type a description or click one of the quick actions below! Switch between Chat mode and Image mode using the buttons at the bottom.`;

// Tips that rotate in the small ribbon at the top of the chat panel.
// First entry is shown on every open; subsequent tips rotate every 12s.
const TIPS = [
  { emoji: '\u{1f41b}', label: 'See something off?', body: 'Tap the red bug button to report it — your team gets a notification within seconds.' },
  { emoji: '\u{2728}', label: 'Quick tip', body: 'Type "find pool projects" to jump straight to matching areas — no scrolling.' },
  { emoji: '\u{1f3a8}', label: 'Heads up', body: 'Switch to Image mode at the bottom to generate renders right inside this chat.' },
  { emoji: '\u{1f4a1}', label: 'Hidden gem', body: 'Drop a photo into the chat and ask me to enhance it — perfect for board decks.' },
  { emoji: '\u{1f41e}', label: 'Found a glitch?', body: 'The bug button is always there — even small reports help us improve faster.' },
];

// Top-level component (NOT defined inside HeavenlyAssistant) so it isn't
// re-created on every parent render — that would force BugReportButton's
// internal modal-open state to reset on each parent re-render.
function BugReportButtonFloating({ token }) {
  return (
    <div
      className="rounded-full p-1.5 shadow-lg"
      style={{ background: 'linear-gradient(135deg, #DC2626 0%, #B91C1C 100%)' }}
      title="Spotted a bug or lag? Tell me about it"
    >
      <BugReportButton token={token} currentLocation={typeof window !== 'undefined' ? window.location.pathname : ''} />
    </div>
  );
}

export default function HeavenlyAssistant({ token }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [provider, setProvider] = useState('openai');
  const [showSettings, setShowSettings] = useState(false);
  const [customKey, setCustomKey] = useState('');
  const [keyEmail, setKeyEmail] = useState('');
  const [genMode, setGenMode] = useState('chat');
  const [welcomed, setWelcomed] = useState(false);
  const [attachment, setAttachment] = useState(null);
  const [tipIdx, setTipIdx] = useState(0);
  const [tipDismissed, setTipDismissed] = useState(false);
  const chatEndRef = useRef(null);
  const sessionId = useRef(`session-${Date.now()}`);
  const mediaRef = useRef(null);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Auto-send welcome message when panel opens
  useEffect(() => {
    if (open && !welcomed) {
      setMessages([{ role: 'assistant', content: WELCOME_MESSAGE, type: 'text' }]);
      setWelcomed(true);
    }
  }, [open, welcomed]);

  // Rotate the tip ribbon every 12 seconds while the panel is open. Bias
  // the rotation toward the bug-report tips (TIPS[0] and TIPS[4]) by
  // showing them at panel-open AND at every 3rd rotation.
  useEffect(() => {
    if (!open || tipDismissed) return;
    setTipIdx(0); // Reset to bug-nudge tip every time the panel reopens.
    const t = setInterval(() => {
      setTipIdx((i) => {
        const next = (i + 1) % TIPS.length;
        return next;
      });
    }, 12000);
    return () => clearInterval(t);
  }, [open, tipDismissed]);

  const auth = { headers: { Authorization: `Bearer ${token}` }, withCredentials: true };

  const handleMediaUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result.split(',')[1];
      setAttachment({
        file,
        base64,
        preview: reader.result,
        type: file.type.startsWith('video') ? 'video' : 'image',
        name: file.name,
      });
    };
    reader.readAsDataURL(file);
    if (mediaRef.current) mediaRef.current.value = '';
  };

  // Heuristic — detects when the user is asking the chat to search for
  // projects (so we route to /projects/search and render Apple-style
  // cards inline) rather than firing an LLM round-trip. Cheap to run on
  // every keystroke; conservative to avoid hijacking real chat queries.
  const detectSearchIntent = (raw) => {
    const t = (raw || '').toLowerCase().trim();
    if (!t) return null;
    // Explicit slash prefix always wins.
    if (t.startsWith('/search ') || t.startsWith('/find ')) {
      return { q: t.replace(/^\/(search|find)\s+/, ''), status: '', kind: '', assignee: '' };
    }
    // Trigger phrases — keep tight so casual chat doesn't get hijacked.
    const triggers = [
      'find ', 'search ', 'show me ', 'list ', 'what is ', "what's ",
      'where is ', "what are ", 'who is working on ', 'show projects',
      'show all projects', 'find projects', 'find areas', 'show widgets',
    ];
    if (!triggers.some((p) => t.startsWith(p))) return null;
    // Pull out simple filters from the phrasing.
    let status = '';
    if (/\b(completed|done|finished)\b/.test(t)) status = 'completed';
    else if (/\b(in[\s-]?progress|ongoing|underway)\b/.test(t)) status = 'in-progress';
    else if (/\b(planned|upcoming|not[\s-]?started|behind schedule)\b/.test(t)) status = 'planned';

    let kind = '';
    if (/\bcrucial\b/.test(t)) kind = 'crucial-enhancement';
    else if (/\b(requested|nice[\s-]?to[\s-]?have|wishlist)\b/.test(t)) kind = 'requested-enhancement';
    else if (/\b(reconstruction|repair|rebuild|pre[\s-]?hurricane)\b/.test(t)) kind = 'reconstruction';

    // Strip the trigger + filter words from `q` so the search is meaningful.
    let q = t;
    triggers.forEach((p) => { q = q.replace(p, ''); });
    q = q.replace(/\b(completed|done|finished|in[\s-]?progress|ongoing|underway|planned|upcoming|not[\s-]?started|crucial|requested|reconstruction|repair|rebuild|pre[\s-]?hurricane|projects?|widgets?|areas?)\b/g, '').trim();
    return { q, status, kind, assignee: '' };
  };

  const sendMessage = async () => {
    if ((!input.trim() && !attachment) || loading) return;
    const msgText = input.trim() || (attachment ? `[Attached: ${attachment.name}]` : '');
    const userMsg = {
      role: 'user',
      content: msgText,
      type: attachment ? 'media' : 'text',
      mediaPreview: attachment?.preview,
      mediaType: attachment?.type,
    };
    setMessages((m) => [...m, userMsg]);
    const currentAttachment = attachment;
    setInput('');
    setAttachment(null);
    setLoading(true);

    try {
      // Natural-language project search shortcut — short-circuits before
      // the LLM round-trip when the user is clearly asking to find work.
      const intent = !currentAttachment && detectSearchIntent(msgText);
      if (intent) {
        setMessages((m) => [...m, { role: 'assistant', content: `Searching projects for "${intent.q || msgText}"…`, type: 'loading' }]);
        const { data } = await axios.get(`${API}/projects/search`, { params: intent });
        setMessages((m) => [
          ...m.filter((msg) => msg.type !== 'loading'),
          {
            role: 'assistant',
            type: 'search-results',
            content: data.count === 0
              ? "I couldn't find anything matching that. Try a broader phrase, like 'show me all reconstruction projects in progress'."
              : `Here are the ${data.count} project${data.count === 1 ? '' : 's'} I found:`,
            results: data.results || [],
          },
        ]);
        setLoading(false);
        return;
      }

      if (genMode === 'image') {
        setMessages((m) => [...m, { role: 'assistant', content: 'Generating image... This may take up to a minute.', type: 'loading' }]);
        const { data } = await axios.post(`${API}/admin/ai/generate-image`, {
          prompt: msgText,
          provider,
          api_key: customKey || undefined,
        }, { ...auth, timeout: 120000 });
        const imgUrl = data.url.startsWith('/') ? `${BACKEND}${data.url}` : data.url;
        setMessages((m) => [...m.filter((msg) => msg.type !== 'loading'), { role: 'assistant', content: imgUrl, type: 'image', text: data.text || 'Here is your generated image!' }]);
      } else {
        const { data } = await axios.post(`${API}/admin/ai/chat`, {
          message: msgText,
          provider,
          session_id: sessionId.current,
          api_key: customKey || undefined,
          image_base64: currentAttachment?.type === 'image' ? currentAttachment.base64 : undefined,
        }, { ...auth, timeout: 60000 });
        setMessages((m) => [...m, { role: 'assistant', content: data.response, type: 'text' }]);
      }
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || 'Something went wrong';
      setMessages((m) => [...m.filter((msg) => msg.type !== 'loading'), { role: 'assistant', content: `Error: ${detail}`, type: 'error' }]);
    } finally {
      setLoading(false);
    }
  };

  const providerLabel = { openai: 'OpenAI GPT-5.2', gemini: 'Gemini 3 Flash' };

  if (!open) {
    return (
      <div className="fixed bottom-20 right-6 z-[9999] flex flex-col items-end gap-2">
        {/* Always-visible bug-report mini button. Floating just above the
            mascot so users see it the moment something feels off — Andres
            wanted Heavenly to actively invite bug reports. */}
        <div className="group/bug">
          <BugReportButtonFloating token={token} />
        </div>
        <button
          data-testid="heavenly-assistant-btn"
          onClick={() => setOpen(true)}
          className="group"
          style={{ filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.3))' }}
        >
          <div className="relative">
            <img src={AVATAR_URL} alt="Heavenly Assistant" className="w-16 h-16 rounded-full object-cover border-2 border-white shadow-xl transition-transform group-hover:scale-110" style={{ animation: 'float 3s ease-in-out infinite' }} />
            <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-green-400 border-2 border-white" />
          </div>
          <span className="absolute -top-10 right-0 bg-white px-3 py-1.5 rounded-xl text-xs font-bold shadow-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none" style={{ color: '#1A2E2E', fontFamily: 'Manrope' }}>
            Heavenly Assistant
          </span>
        </button>
      </div>
    );
  }

  return (
    <div data-testid="heavenly-assistant-panel" className="fixed bottom-20 right-6 z-[9999] flex flex-col" style={{ width: '420px', height: '600px', borderRadius: '20px', overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3" style={{ backgroundColor: '#1A2E2E' }}>
        <img src={AVATAR_URL} alt="Avatar" className="w-10 h-10 rounded-full object-cover border-2 border-white/30" />
        <div className="flex-1">
          <p className="text-sm font-bold text-white" style={{ fontFamily: 'Manrope' }}>Heavenly Assistant</p>
          <p className="text-[10px] text-white/60" style={{ fontFamily: 'Manrope' }}>
            {customKey ? `${providerLabel[provider]} (Your Key)` : `${providerLabel[provider]} (Universal Key)`}
          </p>
        </div>
        <div
          className="rounded-full p-1 shadow-md"
          style={{ background: 'linear-gradient(135deg, #DC2626 0%, #B91C1C 100%)' }}
          title="Spotted a bug? Report it in 5 seconds"
        >
          <BugReportButton token={token} currentLocation={typeof window !== 'undefined' ? window.location.pathname : ''} />
        </div>
        <button onClick={() => setShowSettings(!showSettings)} className="p-1.5 rounded-lg hover:bg-white/10 transition-colors">
          <Settings className="w-4 h-4 text-white/70" />
        </button>
        <button onClick={() => setOpen(false)} className="p-1.5 rounded-lg hover:bg-white/10 transition-colors">
          <X className="w-4 h-4 text-white/70" />
        </button>
      </div>

      {/* Rotating tip ribbon — biased toward bug-report nudges so users
          remember the red button is always available. Dismissable per
          session via the small × button. */}
      {!tipDismissed && (
        <div
          data-testid="heavenly-tip-ribbon"
          className="flex items-center gap-2 px-4 py-2 text-[11px]"
          style={{ background: 'linear-gradient(90deg, #1F4FBE 0%, #003DA5 100%)', color: 'white', fontFamily: 'Manrope' }}
        >
          <span className="text-base leading-none flex-shrink-0">{TIPS[tipIdx].emoji}</span>
          <div className="flex-1 leading-tight">
            <span className="font-bold">{TIPS[tipIdx].label}</span>
            <span className="opacity-90"> · {TIPS[tipIdx].body}</span>
          </div>
          <button
            data-testid="heavenly-tip-dismiss"
            onClick={() => setTipDismissed(true)}
            className="p-0.5 rounded hover:bg-white/10 flex-shrink-0"
            title="Dismiss tips"
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      )}

      {/* Settings Panel */}
      {showSettings && (
        <div className="px-4 py-3 space-y-3" style={{ backgroundColor: '#2a3e3e' }}>
          <div>
            <label className="text-[10px] font-bold text-white/60 block mb-1" style={{ fontFamily: 'Manrope' }}>AI Provider</label>
            <div className="flex gap-2">
              {['openai', 'gemini'].map((p) => (
                <button key={p} onClick={() => setProvider(p)} className="flex-1 px-3 py-1.5 rounded-lg text-xs font-bold transition-all" style={{ backgroundColor: provider === p ? '#1F4FBE' : 'rgba(255,255,255,0.1)', color: 'white', fontFamily: 'Manrope' }}>
                  {p === 'openai' ? 'OpenAI' : 'Gemini'}
                </button>
              ))}
            </div>
          </div>

          {/* API Key Login Section */}
          <div className="p-3 rounded-lg" style={{ backgroundColor: 'rgba(255,255,255,0.05)' }}>
            <div className="flex items-center gap-2 mb-2">
              <Key className="w-3.5 h-3.5 text-white/60" />
              <label className="text-[10px] font-bold text-white/60" style={{ fontFamily: 'Manrope' }}>Login with your AI subscription</label>
            </div>
            <input
              type="email"
              value={keyEmail}
              onChange={(e) => setKeyEmail(e.target.value)}
              placeholder="Your email (for reference)"
              className="w-full px-3 py-1.5 rounded-lg text-xs bg-white/10 text-white border border-white/20 outline-none placeholder-white/30 mb-2"
              style={{ fontFamily: 'Manrope' }}
            />
            <input
              type="password"
              value={customKey}
              onChange={(e) => setCustomKey(e.target.value)}
              placeholder={provider === 'openai' ? 'OpenAI API Key (sk-...)' : 'Gemini API Key'}
              className="w-full px-3 py-1.5 rounded-lg text-xs bg-white/10 text-white border border-white/20 outline-none placeholder-white/30"
              style={{ fontFamily: 'Manrope' }}
            />
            <p className="text-[9px] text-white/40 mt-2" style={{ fontFamily: 'Manrope' }}>
              {customKey
                ? `Using your ${provider === 'openai' ? 'OpenAI' : 'Gemini'} key. Charges go to your subscription.`
                : 'No key entered — using the shared platform key (credits deducted from your account balance).'}
            </p>
            {customKey && (
              <button onClick={() => { setCustomKey(''); setKeyEmail(''); }} className="mt-2 text-[10px] font-bold px-3 py-1 rounded-lg" style={{ backgroundColor: 'rgba(255,99,89,0.2)', color: '#FF6F59', fontFamily: 'Manrope' }}>
                Clear Key (use Universal Key)
              </button>
            )}
          </div>
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3" style={{ backgroundColor: '#F4F1EB' }}>
        {messages.map((msg, idx) => (
          <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start gap-2'}`}>
            {msg.role === 'assistant' && (
              <img src={AVATAR_URL} alt="" className="w-7 h-7 rounded-full object-cover flex-shrink-0 mt-1" />
            )}
            <div className={`max-w-[80%] rounded-2xl px-3.5 py-2.5`} style={{
              backgroundColor: msg.role === 'user' ? '#1F4FBE' : msg.type === 'error' ? '#FFE4E1' : 'white',
              color: msg.role === 'user' ? 'white' : msg.type === 'error' ? '#CC0000' : '#1A2E2E',
            }}>
              {msg.type === 'media' && msg.mediaPreview && (
                <div className="mb-2 rounded-lg overflow-hidden">
                  {msg.mediaType === 'video' ? (
                    <video src={msg.mediaPreview} controls className="w-full max-h-32 rounded-lg" />
                  ) : (
                    <img src={msg.mediaPreview} alt="Attached" className="w-full max-h-32 object-cover rounded-lg" />
                  )}
                </div>
              )}
              {msg.type === 'image' ? (
                <div>
                  <p className="text-xs mb-2" style={{ fontFamily: 'Manrope' }}>{msg.text}</p>
                  <img src={msg.content} alt="Generated" className="w-full rounded-xl" />
                  <a href={msg.content} download className="flex items-center gap-1 mt-2 text-[10px] font-bold" style={{ color: '#1F4FBE' }}>
                    <Download className="w-3 h-3" /> Download
                  </a>
                </div>
              ) : msg.type === 'search-results' ? (
                <div>
                  <p className="text-xs mb-2" style={{ fontFamily: 'Manrope' }}>{msg.content}</p>
                  <div className="space-y-1.5">
                    {(msg.results || []).slice(0, 8).map((r, i) => (
                      <button
                        key={`${r.type}-${r.area_id}-${i}`}
                        data-testid={`heavenly-search-result-${i}`}
                        onClick={() => {
                          window.dispatchEvent(new CustomEvent('open-area', { detail: { areaId: r.area_id } }));
                        }}
                        className="w-full text-left rounded-xl px-3 py-2 transition hover:-translate-y-0.5"
                        style={{
                          backgroundColor: '#F8FAFC',
                          border: '1px solid #E2E8F0',
                        }}
                      >
                        <div className="flex items-center justify-between gap-2 mb-0.5">
                          <span className="text-[8px] uppercase font-bold tracking-wider" style={{ color: r.kind === 'crucial-enhancement' ? '#DC2626' : r.kind === 'requested-enhancement' ? '#F59E0B' : '#0EA5E9' }}>
                            {r.kind === 'crucial-enhancement' ? 'Crucial' : r.kind === 'requested-enhancement' ? 'Requested' : 'Reconstruction'}
                          </span>
                          <span className="text-[8px] uppercase font-bold tracking-wider" style={{ color: r.status === 'completed' ? '#15803D' : r.status === 'in-progress' ? '#B45309' : '#1F4FBE' }}>
                            {r.status.replace('-', ' ')}
                          </span>
                        </div>
                        <p className="text-[11px] font-bold leading-tight" style={{ color: '#1A2E2E', fontFamily: 'Manrope' }}>{r.title}</p>
                        {r.subtitle && (
                          <p className="text-[10px]" style={{ color: '#5C6B6B', fontFamily: 'Manrope' }}>{r.subtitle}</p>
                        )}
                        {r.progress_percent > 0 && (
                          <div className="mt-1 h-1 rounded-full overflow-hidden" style={{ backgroundColor: '#E5E7EB' }}>
                            <div className="h-full rounded-full" style={{ width: `${r.progress_percent}%`, backgroundColor: r.kind === 'crucial-enhancement' ? '#DC2626' : r.kind === 'requested-enhancement' ? '#F59E0B' : '#0EA5E9' }} />
                          </div>
                        )}
                      </button>
                    ))}
                    {msg.results.length > 8 && (
                      <p className="text-[10px] text-center" style={{ color: '#5C6B6B', fontFamily: 'Manrope' }}>
                        +{msg.results.length - 8} more · open Project Search to see all
                      </p>
                    )}
                  </div>
                </div>
              ) : msg.type === 'loading' ? (
                <div className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" style={{ color: '#1F4FBE' }} />
                  <span className="text-xs" style={{ fontFamily: 'Manrope' }}>{msg.content}</span>
                </div>
              ) : (
                <p className="text-xs leading-relaxed whitespace-pre-wrap" style={{ fontFamily: 'Manrope' }}>{msg.content}</p>
              )}
            </div>
          </div>
        ))}

        {messages.length <= 1 && (
          <div className="flex flex-wrap justify-center gap-2 pt-2">
            {[
              { label: 'Generate a pool render', mode: 'image' },
              { label: 'Create a lobby floor plan', mode: 'image' },
              { label: 'Visualize rooftop garden', mode: 'image' },
              { label: 'Resort renovation tips', mode: 'chat' },
            ].map((s) => (
              <button key={s.label} onClick={() => { setInput(s.label); setGenMode(s.mode); }} className="px-3 py-1.5 rounded-full text-[10px] font-bold transition-all hover:scale-105" style={{ backgroundColor: '#EAE5D9', color: '#4A5D5D', fontFamily: 'Manrope' }}>{s.label}</button>
            ))}
          </div>
        )}
        <div ref={chatEndRef} />
      </div>

      {/* Attachment Preview */}
      {attachment && (
        <div className="px-3 pt-2 flex items-center gap-2" style={{ backgroundColor: 'white' }}>
          <div className="relative h-12 w-16 rounded-lg overflow-hidden flex-shrink-0">
            {attachment.type === 'video' ? (
              <video src={attachment.preview} className="w-full h-full object-cover" />
            ) : (
              <img src={attachment.preview} alt="Attached" className="w-full h-full object-cover" />
            )}
            <button onClick={() => setAttachment(null)} className="absolute -top-0.5 -right-0.5 w-4 h-4 rounded-full bg-red-500 text-white flex items-center justify-center">
              <X className="w-2.5 h-2.5" />
            </button>
          </div>
          <span className="text-[10px] truncate" style={{ color: '#7A8C8C', fontFamily: 'Manrope' }}>{attachment.name}</span>
        </div>
      )}

      {/* Input */}
      <div className="px-3 py-3 flex items-center gap-2" style={{ backgroundColor: 'white', borderTop: attachment ? 'none' : '1px solid #EAE5D9' }}>
        <div className="flex gap-1">
          <button
            onClick={() => setGenMode('chat')}
            className="p-2 rounded-lg text-xs font-bold transition-all"
            style={{ backgroundColor: genMode === 'chat' ? '#1F4FBE' : '#EAE5D9', color: genMode === 'chat' ? 'white' : '#4A5D5D' }}
            title="Chat mode"
          >
            <Send className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={() => setGenMode('image')}
            className="p-2 rounded-lg text-xs font-bold transition-all"
            style={{ backgroundColor: genMode === 'image' ? '#1F4FBE' : '#EAE5D9', color: genMode === 'image' ? 'white' : '#4A5D5D' }}
            title="Image generation mode"
          >
            <Image className="w-3.5 h-3.5" />
          </button>
          <label className="p-2 rounded-lg text-xs font-bold transition-all cursor-pointer" style={{ backgroundColor: attachment ? '#1F4FBE' : '#EAE5D9', color: attachment ? 'white' : '#4A5D5D' }} title="Attach photo or video">
            <Paperclip className="w-3.5 h-3.5" />
            <input ref={mediaRef} type="file" accept="image/*,video/*" className="hidden" onChange={handleMediaUpload} />
          </label>
        </div>
        <input
          data-testid="ai-chat-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && sendMessage()}
          placeholder={genMode === 'image' ? 'Describe the image to generate...' : 'Ask me anything...'}
          className="flex-1 px-3 py-2 rounded-lg border text-xs outline-none"
          style={{ borderColor: '#A3D4D4', fontFamily: 'Manrope' }}
          disabled={loading}
        />
        <button
          data-testid="ai-send-btn"
          onClick={sendMessage}
          disabled={loading || (!input.trim() && !attachment)}
          className="p-2 rounded-lg text-white transition-all disabled:opacity-50"
          style={{ backgroundColor: '#1F4FBE' }}
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
}
